import React, { useCallback, useState } from 'react';

import { useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard/shared/useDialogWizardState';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';

import { ModifyUserFormWizardScreen } from './screens/modify-user-form-wizard-screen';
import { useModifyUserContext } from '../context/modify-user-context';
import { UserUpdatedWizardScreen } from './screens/user-updated-wizard-screen';
import { UserUpdatingWizardScreen } from './screens/user-updating-wizard-screen';

interface ModifyUserContainerProps {
  onClose: () => void;
}

export const ModifyUserContainer = ({ onClose }: ModifyUserContainerProps) => {
  const { form, user, view } = useModifyUserContext();
  const [error, setError] = useState<Error | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = useCallback(async () => {
    setError(null);
    setIsSaving(true);

    try {
      await view.modifyUser(user, {
        email: form.email,
        fullName: form.fullName,
        roleId: form.roleId,
        selectedOrganisations: form.selectedOrganisations
      });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError : new Error('Unable to update user'));
    } finally {
      setIsSaving(false);
    }
  }, [form, user, view]);

  const { handleAction, screen } = useDialogWizardState({
    error,
    isLoading: isSaving,
    onAction: handleSubmit,
    onClose,
    validate: form.validate
  });

  if (view.state.isLoading) {
    return <LoadingWizardScreen />;
  }

  switch (screen) {
    case 'loading':
      return <UserUpdatingWizardScreen />;
    case 'success':
      return <UserUpdatedWizardScreen onClose={onClose} />;
    default:
      return (
        <ModifyUserFormWizardScreen
          error={(error || view.state.errorLoading)?.message}
          onClose={onClose}
          onUpdate={handleAction}
        />
      );
  }
};
